import { Fragment, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type CSSProperties, type KeyboardEvent, type MutableRefObject, type ReactNode } from 'react';
import { useInRouterContext } from 'react-router-dom';
import {
  Color,
  PAGE_SIZES,
  Size,
  alignmentFor,
  compactLineCount,
  compactFigure,
  formatListCell,
  displayModeFor,
  emptySelection,
  groupRows,
  heldDraftPill,
  isSelected,
  nestedRows,
  offersSelectAllMatching,
  pageCount,
  pageSelected,
  pageSlice,
  selectionCount,
  selectionReducer,
  sortRows,
  t,
  toggleExpanded,
  toggleSort,
  type AsyncState,
  type ColumnDef,
  type DisplayMode,
  type NestedRow,
  type PageSize,
  type Selection,
  type SortState,
  type StatusResolver,
  type SuggestionRow, ALL_VIEW_ID, matchesCriteria, resolveLinkPattern, viewColumns, type DateFormat } from '@bms2/shared';
import { Button, Checkbox, Dropdown, IconButton, Pill, SelectChevron, StatusPill } from '../controls';
import fieldStyles from '../controls/field.module.css';
import { Icon } from '../Icon';
import { LIST_GROUP_FOLD_KEY_PREFIX } from '../../shell/devicePreference';
import { FoldChevron, useFoldMemory } from './FoldChevron';
import { TreeChildCount, TreeToggle } from './TreeList';
import { fileThumbPath } from '../files/filePaths';
import { MoneyText } from '../Money';
import { AsyncStatePanel } from './AsyncStatePanel';
import { SavedViewAddress, SavedViewWindow, useSavedViews, type StoredView } from './savedViews';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import styles from './ListViewer.module.css';
import { useMeasuredWidth } from '../useMeasuredWidth';

/**
 * List Viewer (piece 4): the one reusable table, three parts always assembled
 * as one unit: the view-switcher Dropdown, the record-count text and a real
 * `<table>` (decision 0070). It fills the height it is given and never
 * shrink-wraps; it paginates at 50, 100 or 200 rows and never virtualizes;
 * it carries row multi-select as ids or the view's filter plus exclusions
 * (decision 0244), with an action bar that holds the count and Clear and
 * nothing else; Compact switches on by measured width below the 320px token
 * and replaces the header row with a band naming the record type, or is pinned
 * by a list spec whose secondary column is prose (decision 2852).
 */
export const listViewerDocumentedStates = ['wide', 'twoRows', 'compact', 'grouped', 'selected', 'loading', 'empty', 'failed'] as const;

export interface ListView {
  id: string;
  name: string;
  /** A starred view draws its star and leads the switcher after All (decision 2171). */
  isFavourite?: boolean;
  /** The view the list opens on reads *(opens here)* after its name (decision 2171). */
  isDefault?: boolean;
  /** The person's own view, or a built-in view whose columns are theirs, which carries the pencil opening Edit View (decisions 2170, 2981). */
  editable?: boolean;
  /** 🔒 A built-in view of the list's register: its pencil edits the columns alone, and an empty answer offers no Edit View link (decision 2981). */
  builtIn?: boolean;
  /** The view has conditions, so a list it empties says so rather than inviting the first record (decision 2172). */
  narrows?: boolean;
}

/** A stored view as the switcher lists it (decisions 2170, 2171). */
export function switcherView(view: StoredView): ListView {
  return { id: view.id, name: view.name, isFavourite: view.isFavourite, isDefault: view.isDefault, editable: true, narrows: view.criteria.length > 0 };
}

export interface ListViewerStatus {
  resolve: StatusResolver;
  /** The enum a status column is typed as, by column key. */
  entityTypeFor: (columnKey: string) => string;
}

export interface ListViewerProps<R> {
  /** The record type the Compact band names, such as Companies. */
  recordType: string;
  views: readonly ListView[];
  currentViewId: string;
  onViewChange: (viewId: string) => void;
  /** Opens the New View Modal, where the host keeps its own store. */
  onNewView?: () => void;
  /** Opens Edit View on one of the person's own views, where the host keeps its own store (decision 2170). */
  onEditView?: (viewId: string) => void;
  /**
   * 🔒 The list's own scope in the one store (decision 2174), for a list whose
   * host keeps none: the viewer then reads, writes, edits and deletes the
   * person's views itself, opens on their default and carries the chosen view
   * in the address under `param`, `savedView` unless a page holding two tables
   * names another. 🔒 A list with neither this nor `onNewView` offers no Add New View.
   */
  viewStore?: { scopeKey: string; apiBaseUrl: string; param?: string };
  /** The address the `image` cells' file route reads from (T3333); a list without an image column needs none. */
  imageBaseUrl?: string;
  /** The record type in the singular, for *No company matches this view* (decision 2172). */
  recordSingular?: string;
  columns: readonly ColumnDef[];
  /** The matching set; the component sorts it totally and slices the page. Under `serverPage` it is the page itself, already sorted. */
  rows: readonly R[];
  idOf: (row: R) => string;
  read: (row: R, key: string) => unknown;
  /** The real count from the count query; the matching set's length when omitted. */
  total?: number;
  /** Sort state belongs to the saved view, so the view owns it. */
  sort: SortState | null;
  onSortChange: (sort: SortState) => void;
  /** Remembered per saved view in the per-user preference store. */
  pageSize: PageSize;
  onPageSizeChange: (size: PageSize) => void;
  /**
   * Banded group headings in the caller's order (piece 4). 🔒 A key named
   * `headless` draws its rows with no heading row, where its one row names
   * itself — a report's result line (decision 0880).
   */
  groupBy?: { key: string; order: readonly string[]; headless?: readonly string[] };
  status?: ListViewerStatus;
  /** Loading, empty and failed; ready renders the rows. */
  state?: AsyncState<unknown>;
  emptyAction: string;
  /**
   * 🔒 A Filter, a Series choice or a search that matches nothing reads "No item matches this filter" with Clear Filter beside it,
   * never the empty list's invitation (piece 13; T3289). Given only while one of them stands; the list draws it over the view's own line.
   */
  filterNoMatch?: { onClear: () => void; /** True where a search alone narrows, so the line reads as a search (T3341). */ searchOnly?: boolean };
  onRetry?: () => void;
  onNavigate?: (row: R) => void;
  /** Which rows open: a row it answers false for draws no Open control and a click on it does nothing, such as a statement's total (T3445). Absent, every row opens. */
  opensRow?: (row: R) => boolean;
  /** Where a `link` column's cell goes (decision 0565); without it the cell is its text alone. */
  onRoute?: (route: string) => void;
  /**
   * 🔒 A row that opens in place (decision 4811): a click or Enter on the row toggles it, and an open row
   * draws its cut cells whole, wrapped. A list that names none keeps every row one line.
   */
  expandRow?: { isOpen: (row: R) => boolean; onToggle: (row: R) => void };
  /** The row a list-and-detail page is showing beside the list, marked so the eye finds it (piece 17; decision 0318). */
  activeId?: string | null;
  currencyCode?: string;
  /** A selection to open with, for the harness. */
  initialSelection?: Selection;
  /** The ids selected now, for a list page whose actions run over the selection (piece 17's Change accounts). */
  onSelectionChange?: (ids: readonly string[]) => void;
  /**
   * A number the host bumps to clear the selection without remounting the table (T3676): the sort, the filter and the scroll stay,
   * and only the ticks go. The first value clears nothing.
   */
  clearSelectionKey?: number;
  /**
   * 🔒 The selection as it stands and how many rows it names (decision 7780): the page's ids, or the
   * view's filter less its exclusions after Select all N matching. A host whose server cuts the page
   * reads this, because the rows a filter selection names are not all in the browser.
   */
  onSelection?: (selection: Selection, count: number) => void;
  /** A measured width for the suite and the harness; the component measures itself otherwise. */
  widthOverride?: number;
  /**
   * 🔒 Pins Compact at every width (decision 2852; T2059): the compact rows, no header row, the
   * view's own sort and no band naming the type. Absent, the width decides (decision 0259).
   */
  layout?: 'compact' | undefined;
  /** A row drawn in bold, such as an unread message (piece 32); a list that names none draws every row alike. */
  isEmphasised?: (row: R) => boolean;
  /**
   * 🔒 The actions a piece binds to the action bar (decision 0244), drawn between the count and Clear while a
   * selection stands: the mail client's move, mark read and delete are the first (piece 32).
   */
  selectionActions?: ReactNode;
  /**
   * 🔒 A row drawn in a style the caller names (decision 0880): the class goes on
   * the row and the name on its `data-row-style`, in Wide and Compact mode alike.
   */
  rowStyle?: (row: R) => { name: string; className: string } | null;
  /**
   * 🔒 Rows drawn after the last row of every page in the same row markup
   * (decision 0880): a report's foot. They are never sorted, paged, counted or
   * selected, so sorting the table can never move a total among the rows.
   */
  footRows?: readonly R[];
  /**
   * 🔨 The sort is locked, with the reason from the string table (decision 0880):
   * the headers draw as labels carrying the reason, and the rows keep the order
   * the caller handed them in. A statement's lines are its own arrangement.
   */
  sortLocked?: string;
  /**
   * 🔒 The server sorts (decision 10062): the rows arrive in the order the sort asks for, the headers stay live and the
   * viewer re-sorts nothing. A drilled General Ledger account keeps its Opening and Closing rows where the server put them.
   */
  serverSort?: boolean;
  /**
   * 🔒 Whether the rows take a selection at all (decision 1002; T349). A list with no
   * action to run over its rows, a report's table first, passes false and draws
   * no selection column, no Select all and no action bar. Every list is
   * selectable where it says nothing, as every list was before.
   */
  selectable?: boolean;
  /**
   * 🔒 A word drawn after one cell's figure, where the caller names one
   * (decision 1140): a report's *(credit)* on a credited cost line. A cell it
   * names nothing for draws as it always did.
   */
  cellMark?: (row: R, key: string) => string | null;
  /**
   * 🔒 A mark drawn after one cell's text where the caller names one (T3028), and the whole of a `marks` cell (decision
   * 9720; T3164): the gold dot and a missing tax form's triangle in the Marks column, drawn in place of its words.
   */
  cellMarker?: (row: R, key: string) => ReactNode;
  /**
   * 🔒 A hover sentence for one text cell where the caller names one (T3866; decision 12911): it becomes the cell's `title` and
   * its `aria-description`, so a screen reader reads it with the cell. A cell it names nothing for keeps its own text as the title.
   */
  cellTitle?: (row: R, key: string) => string | null;
  /**
   * 🔒 The compact band naming the record type is dropped where the host already
   * heads the list, so a list pane names its type once, not twice (decision 1444;
   * T691).
   */
  bandless?: boolean;
  /**
   * 🔒 What the host draws at the start of the list's top row, beside the view
   * picker: the Mail list's one search field (decision 1453). Absent, the row
   * holds the picker alone as before.
   */
  leading?: ReactNode;
  /**
   * 🔒 The list's filter controls, drawn after the view picker on the same row:
   * the Deals list's Tags menu (T1581, screen E). Absent, the row holds what it did.
   */
  filters?: ReactNode;
  /**
   * 🔒 The star in the switcher is a toggle where the host keeps the stars
   * (decision 2880): pressing it stars or unstars that view, built-in or the
   * person's own, and never chooses it. Absent, a starred view draws its star alone.
   */
  onStarView?: (viewId: string) => void;
  /** 🔒 What the switcher says under itself, such as a sixth star refused by name (decision 2880). */
  viewNotice?: string | null;
  /**
   * 🔒 The server cuts the page (decision 1890): `rows` is the page it
   * answered, sorted by the column the host asked for, `total` is the whole
   * matching set's size, and the pager asks the host for another page rather
   * than slicing. 🔒 Select all N matching is offered here too (decision 7780,
   * amending 1890): a move to another page clears a page selection and keeps a
   * filter selection, and a change of `scope`, the host's read without its page,
   * clears either.
   */
  serverPage?: { page: number; onPageChange: (page: number) => void; scope?: string };
  /**
   * 🔒 A row that came from another row of the list nests under it (decision 8880; piece 4), where the caller
   * names each row's parent by its id: the Item page's Transactions tile draws a Bill under its Purchase Order.
   * A row whose parent is not among the rows stands at the top. The sort orders the top rows and each parent's
   * children among themselves, so a child never leaves its parent; the parent draws the tree list's chevron and
   * child count in its primary cell and opens expanded, and a child the tree list's rail. The count counts every row.
   */
  nest?: { parentOf: (row: R) => string | null };
  /**
   * 🔒 Told where the rows' scroll box stands each time it scrolls, and handed back the place to
   * stand on when the viewer is drawn again (decision 7750; T3123): a list that moves into a pane
   * remounts its viewer, and the host holds the place across it.
   */
  onScrollTop?: (top: number) => void;
  initialScrollTop?: number;
}

const noop = () => undefined;

/**
 * 🔒 A nested cell's step and rails (decision 8880): the cell starts one indent deeper per level, and draws the
 * tree list's rail under each ancestor's chevron down its full height, and an open parent its own rail from
 * under its chevron to the row's foot, so the line meets its first child. The rails take the neutral colour,
 * as a tree whose rows carry none does (decision 1420).
 */
function nestStyle(at: { depth: number; open: boolean }): CSSProperties {
  const layers = [
    ...Array.from({ length: at.depth }, (_, level) => ({ level, y: '0', height: '100%' })),
    ...(at.open ? [{ level: at.depth, y: '100%', height: 'calc(50% - var(--icon-size-nav) / 2)' }] : []),
  ];
  return {
    '--tree-depth': at.depth,
    ...(layers.length === 0 ? {} : {
      '--tree-rail-images': layers.map(() => 'linear-gradient(var(--tree-rail-neutral), var(--tree-rail-neutral))').join(', '),
      '--tree-rail-positions': layers.map((layer) => `calc(var(--tree-indent) * ${layer.level} + var(--nest-rail-offset)) ${layer.y}`).join(', '),
      '--tree-rail-sizes': layers.map((layer) => `var(--tree-rail-width) ${layer.height}`).join(', '),
    }),
  } as CSSProperties;
}

/** The two things a date cell reads besides its value: the Organization's format and the viewer's zone (pieces 12, 15). */
interface DateReading {
  format: DateFormat;
  zone: string;
}

/** 🔒 One cell as text is the shared formatter's (decisions 0481, 1260, 1444), the one the phone's lists read too (decision 3420). */
function formatCell(column: ColumnDef, value: unknown, currencyCode: string, dates: DateReading): string {
  return formatListCell(column, value, { zone: dates.zone, dateFormat: dates.format, currencyCode });
}

/** A text cell's hover: the caller's sentence, which a screen reader reads too (T3866), else the cell's own text. */
function cellTitleProps(named: string | null, text: string): { title?: string | undefined; 'aria-description'?: string } {
  if (named) return { title: named, 'aria-description': named };
  return { title: text || undefined };
}

/** 🔒 One threshold for both flips (T593): a pixel of slack for offsetWidth's sub-pixel rounding, the same test in both directions. */
function overflowsPane(natural: number, room: number): boolean {
  return natural > room + 1;
}

/** 🔒 T593: a count that moves once the page's fonts finish loading, where the browser reports it; a font arriving late changes every width measured before it. */
function useFontsLoaded(): number {
  const [loaded, setLoaded] = useState(0);
  useEffect(() => {
    const fonts = typeof document === 'undefined' ? undefined : (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts || fonts.status === 'loaded') return;
    let live = true;
    void fonts.ready.then(() => {
      if (live) setLoaded((count) => count + 1);
    });
    return () => {
      live = false;
    };
  }, []);
  return loaded;
}

/** 🔒 T593: a short fingerprint of the text a page of rows draws, so a row whose text changed under the same id is a different table to measure. */
function rowTextKey(texts: readonly string[]): string {
  let hash = 2166136261;
  for (const text of texts) {
    for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
    hash = Math.imul(hash ^ 31, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value) ?? '';
    } catch {
      // silent: a value that cannot be written as JSON is written by its type alone.
      return String(value);
    }
  }
  return String(value);
}

/**
 * 🔒 Decision 1402: a Wide secondary column is its content's width up to the cap. When the
 * columns' natural widths overflow the pane, the table falls back to the cap as a share that
 * shrinks, so a narrow pane truncates in the same order as before and never scrolls sideways.
 * The natural width is measured with the fallback off, and re-measured when the rows, the
 * columns or the width change.
 *
 * 🔒 T593: the verdict also starts roomy again, and so measures again, when a row's text
 * changes under the same ids, when the panel turns ready with the same rows, and once the
 * page's fonts have loaded; a verdict kept across any of those measured a table that is no
 * longer drawn. One threshold decides both flips, so a pane between the two cannot hold
 * whichever verdict it arrived with.
 */
function useTightColumns(table: MutableRefObject<HTMLTableElement | null>, box: MutableRefObject<HTMLDivElement | null>, mode: DisplayMode, width: number | null, dataKey: string): boolean {
  // The verdict belongs to the table it was measured on; a change of its rows, columns, text, readiness or fonts starts roomy again.
  const key = `${dataKey}~${useFontsLoaded()}`;
  const [verdict, setVerdict] = useState<{ key: string; tight: boolean }>({ key, tight: false });
  const tight = verdict.key === key && verdict.tight;
  const natural = useRef(0);
  useLayoutEffect(() => {
    const t = table.current;
    const b = box.current;
    if (mode !== 'wide' || !t || !b) return;
    const room = b.clientWidth;
    if (!tight) {
      natural.current = t.offsetWidth;
      if (overflowsPane(natural.current, room)) setVerdict({ key, tight: true });
    } else if (!overflowsPane(natural.current, room)) {
      setVerdict({ key, tight: false });
    }
  }, [table, box, mode, width, key, tight]);
  return tight;
}

export function ListViewer<R>(props: ListViewerProps<R>) {
  const dates: DateReading = { format: useDateFormat(), zone: useViewerZone() };
  const {
    recordType, imageBaseUrl = '', views, currentViewId, onViewChange, onNewView, onEditView, viewStore, recordSingular, columns: allColumns, rows: allRows, idOf, read, total, sort, onSortChange, pageSize, onPageSizeChange,
    groupBy, status, state, emptyAction, filterNoMatch, onRetry, onNavigate, opensRow, onRoute, expandRow, activeId, currencyCode = 'USD', initialSelection, clearSelectionKey, widthOverride, layout, onSelectionChange, onSelection, isEmphasised, selectionActions,
    rowStyle, footRows = [], sortLocked, serverSort = false, selectable = true, cellMark, cellMarker, cellTitle, bandless = false, leading, filters, serverPage, onStarView, viewNotice = null, onScrollTop, initialScrollTop = 0, nest,
  } = props;
  /**
   * 🔒 The list's own views in the one store, where the host keeps none
   * (decision 2174, amending 0874's for-the-visit rule). A view applies its
   * columns in its own order and its conditions to the rows this list holds,
   * and choosing it restores its sort and rows per page.
   */
  const store = useSavedViews({
    scopeKey: viewStore?.scopeKey ?? null,
    apiBaseUrl: viewStore?.apiBaseUrl ?? '',
    onApply: (view) => {
      if (view.sort && sortLocked === undefined && !serverSort) onSortChange(view.sort);
      if (view.pageSize) onPageSizeChange(view.pageSize);
    },
  });
  const inRouter = useInRouterContext();
  const ownView = viewStore ? store.current : null;
  const columns = useMemo(() => (ownView ? viewColumns(allColumns, ownView.columns) : allColumns), [ownView, allColumns]);
  const narrowedHere = ownView !== null && ownView.criteria.length > 0;
  // 🔨 The derived sets below are memoised, so a selection click or an unrelated state change does not sort, hash or re-read the whole row set (T649).
  const rows = useMemo(
    () => (narrowedHere
      ? allRows.filter((row) => matchesCriteria(Object.fromEntries(allColumns.map((column) => [column.key, read(row, column.key)])), ownView.criteria, { zone: dates.zone }))
      : allRows),
    [narrowedHere, allRows, allColumns, read, ownView, dates.zone],
  );
  const [cardRef, width] = useMeasuredWidth<HTMLDivElement>({ override: widthOverride });
  const mode: DisplayMode = layout === 'compact' ? 'compact' : width === null ? 'wide' : displayModeFor(width);
  const [selection, dispatch] = useReducer(selectionReducer, initialSelection ?? emptySelection);
  const [page, setPage] = useState(1);
  const allIds = useMemo(() => rows.map(idOf), [rows, idOf]);
  const selectedKey = useMemo(
    () => (selection.mode === 'ids' ? allIds.filter((id) => selection.ids.has(id)) : allIds.filter((id) => !selection.exclusions.has(id))).join('|'),
    [selection, allIds],
  );
  useEffect(() => {
    onSelectionChange?.(selectedKey ? selectedKey.split('|') : []);
    // The callback is the page's; the key is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);
  const shownViewId = ownView?.id ?? currentViewId;
  const previousView = useRef(shownViewId);
  /* 🔒 A change of what the host reads clears the selection, so a filter selection never outlives the filter it was made on (decision 7780). */
  const readScope = serverPage?.scope;
  const previousScope = useRef(readScope);
  useEffect(() => {
    if (previousScope.current !== readScope) {
      previousScope.current = readScope;
      dispatch({ type: 'clear' });
    }
  }, [readScope]);

  const previousClearKey = useRef(clearSelectionKey);
  useEffect(() => {
    if (previousClearKey.current !== clearSelectionKey) {
      previousClearKey.current = clearSelectionKey;
      dispatch({ type: 'clear' });
    }
  }, [clearSelectionKey]);

  useEffect(() => {
    if (previousView.current !== shownViewId) {
      previousView.current = shownViewId;
      dispatch({ type: 'viewSwitched' });
      setPage(1);
    }
  }, [shownViewId]);

  // 🔨 A locked sort keeps the caller's order exactly, the id tiebreaker included (decision 0880); a page the server cut is already in the order asked for (decision 1890).
  const sorted = useMemo(() => (sortLocked !== undefined || serverSort || serverPage ? [...rows] : sortRows(rows, sort, read, idOf)), [sortLocked, serverSort, serverPage, rows, sort, read, idOf]);
  /* 🔒 The parents the person folded, for the visit; every parent opens expanded (decision 8880). */
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  /*
   * 🔒 A parent whose children all leave the rows forgets its fold, so it opens unfolded when they come back
   * (decision 8880; T3344): a row that opens in place to its children keeps one id, and focus, either way.
   */
  const parentIds = nest ? [...new Set(rows.map(nest.parentOf).filter((parent): parent is string => parent !== null))].sort().join('\n') : '';
  const [foldParents, setFoldParents] = useState(parentIds);
  if (foldParents !== parentIds) {
    setFoldParents(parentIds);
    const parents = new Set(parentIds.split('\n'));
    const kept = [...folded].filter((id) => parents.has(id));
    if (kept.length !== folded.size) setFolded(new Set(kept));
  }
  // 🔨 A nested list is never grouped: the groups would split a child from its parent (piece 4; T3293).
  const nested = useMemo(() => (nest && !groupBy ? nestedRows(sorted, idOf, nest.parentOf, folded) : null), [nest, groupBy, sorted, idOf, folded]);
  const nestOf = useMemo(() => new Map<string, NestedRow<R>>((nested ?? []).map((at) => [idOf(at.row), at] as const)), [nested, idOf]);
  const drawn = useMemo(() => (nested ? nested.map((at) => at.row) : sorted), [nested, sorted]);
  const count = total ?? rows.length;
  // A folded parent hides its children from the pages, while the count still counts every row.
  const pages = pageCount(nested && !serverPage ? drawn.length : count, pageSize);
  const currentPage = Math.min(serverPage ? serverPage.page : page, pages);
  /** A move to another page: the host's read where the server cuts the page, and the selection with it (decision 1890); the viewer's own slice otherwise. */
  const goToPage = (next: number) => {
    if (!serverPage) return setPage(next);
    // 🔒 A filter selection names every matching row, so it stands across the pages (decision 7780).
    if (selection.mode === 'ids') dispatch({ type: 'clear' });
    serverPage.onPageChange(next);
  };
  const ordered = useMemo(() => (groupBy ? groupRows(drawn, (r) => String(read(r, groupBy.key) ?? ''), groupBy.order).flatMap((g) => g.rows) : drawn), [groupBy, drawn, read]);
  const pageRows = useMemo(() => (serverPage ? ordered : pageSlice(ordered, currentPage, pageSize)), [serverPage, ordered, currentPage, pageSize]);
  const pageIds = useMemo(() => pageRows.map(idOf), [pageRows, idOf]);
  // 🔒 Decision 6211, T2907: a group header counts the group's rows in the whole list, not the page's slice of it; where the server cuts the page the group's size is unknown, so it draws no count.
  const groupSizes = groupBy && !serverPage ? new Map(groupRows(sorted, (r) => String(read(r, groupBy.key) ?? ''), groupBy.order).map((g) => [g.key, g.rows.length] as const)) : null;
  const groups = groupBy ? groupRows(pageRows, (r) => String(read(r, groupBy.key) ?? ''), groupBy.order).filter((g) => g.rows.length > 0) : [{ key: '', rows: pageRows }];
  const selected = selectionCount(selection, count);
  useEffect(() => {
    onSelection?.(selection, selected);
    // The callback is the host's; the selection and its count are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, selected]);
  const allOnPage = pageSelected(selection, pageIds);
  const someOnPage = !allOnPage && pageIds.some((id) => isSelected(selection, id));
  const panelState: AsyncState<unknown> = state ?? { kind: 'ready', data: rows };
  const tableRef = useRef<HTMLTableElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  /* 🔒 The place the rows stood at, drawn again once they are in and after each change of mode, which re-lays them out (decision 7750). */
  const standAt = useRef(initialScrollTop);
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (box && panelState.kind === 'ready' && standAt.current > 0 && box.scrollTop !== standAt.current) box.scrollTop = standAt.current;
  }, [mode, panelState.kind]);
  const rowText = useMemo(() => rowTextKey(pageRows.flatMap((row) => columns.map((column) => cellText(read(row, column.key))))), [pageRows, columns, read]);
  const tight = useTightColumns(tableRef, scrollRef, mode, width, `${columns.map((c) => c.key).join('|')}#${pageIds.join('|')}#${rowText}#${panelState.kind}`);

  const currentView = ownView ?? views.find((v) => v.id === currentViewId);
  // 🔒 All first, then the starred views in name order, then the rest (decision 2171).
  const everyView: ListView[] = [...views, ...(viewStore ? store.ordered.map(switcherView) : [])];
  const viewNamed = (id: string) => everyView.find((v) => v.id === id);
  const editView = onEditView ?? (viewStore ? store.openEdit : undefined);
  const addView = onNewView ?? (viewStore ? store.openNew : null);
  const searchViews = (query: string): Promise<SuggestionRow[]> => {
    const q = query.trim().toLowerCase();
    // The field holds the chosen view's own name, so opening it from the chevron lists every view rather than the one already chosen (decision 0296).
    const chosen = currentView?.name.toLowerCase() ?? '';
    const listAll = q === '' || q === chosen;
    return Promise.resolve(everyView.filter((v) => listAll || v.name.toLowerCase().includes(q)).map((v) => ({ id: v.id, line1: v.isDefault ? `${v.name} ${t('view.opensHere')}` : v.name })));
  };
  const chooseView = (id: string) => {
    if (viewStore && store.views.some((view) => view.id === id)) return store.choose(id);
    // A list view of the host's own, such as All: the store says All, so a default view does not reopen over it.
    if (viewStore) store.choose(ALL_VIEW_ID);
    onViewChange(id);
  };
  // 🔒 A view whose conditions match nothing says so, with the list's own noun and an Edit View link (decision 2172).
  const shownView = viewNamed(shownViewId);
  const viewNarrows = narrowedHere || shownView?.narrows === true;

  // 🔒 The count stands alone in the footer; the page-of reads in the pager beside it (decision 1444; T691).
  // 🔒 A view applied to the rows the browser holds says the count is of those rows (decision 2174).
  const countText = `${new Intl.NumberFormat('en-US').format(count)} ${t(count === 1 ? 'list.record' : 'list.records')}${narrowedHere ? ` ${t('list.viewHeld')}` : ''}`;
  const selectedText = `${new Intl.NumberFormat('en-US').format(selected)} ${t('list.selected')}`;
  const pageCountText = `${new Intl.NumberFormat('en-US').format(pageIds.length)} ${t('list.onPage')}`;
  const selectAllText = `${t('list.selectAll')} ${new Intl.NumberFormat('en-US').format(count)} ${t('list.matching')}`;
  const pageText = `${t('list.page')} ${currentPage} ${t('list.of')} ${pages}`;
  const primary = columns.find((c) => c.role === 'primary');
  // 🔒 A picture and a row's marks are no words of the compact second line (decisions 9380, 9720).
  const secondaries = columns.filter((c) => c.type !== 'image' && c.type !== 'marks' && (c.role === 'secondary' || c.role === 'date'));
  const imageColumn = columns.find((c) => c.type === 'image');
  const marksColumn = columns.find((c) => c.type === 'marks');
  // 🔒 Decision 1544: a list whose secondary figures carry their names wraps that line, so none is cut off; every other list keeps one truncated line.
  const wrapCompact = secondaries.some((c) => Boolean(c.compactLabel) || c.compactWrap === true);
  const statusColumn = columns.find((c) => c.role === 'status');
  const valueColumn = columns.find((c) => c.role === 'value');
  const wideSpan = columns.length + (selectable ? 1 : 0) + (onNavigate ? 1 : 0);

  const renderStatus = (column: ColumnDef, row: R): ReactNode => {
    const value = read(row, column.key);
    if (value === null || value === undefined) return null;
    if (!status) return String(value);
    // 🔒 A Draft the gate holds draws its approval's pill in place of Draft (T1195; decision 1861).
    const held = column.heldByKey ? heldDraftPill(String(value), String(read(row, column.heldByKey) ?? '')) : null;
    const resolve = held ? () => ({ label: held.text, colorGroup: held.group }) : status.resolve;
    return <StatusPill entityType={status.entityTypeFor(column.key)} statusValue={String(value)} resolve={resolve} size={Size.Small} />;
  };
  /**
   * 🔒 A status column on the compact secondary line reads its seeded label, not
   * its stored member (decision 0639): the line is text, so the word is the
   * row's, and *NotStarted* is a word nobody wrote.
   */
  const secondaryText = (column: ColumnDef, row: R): string => {
    const value = read(row, column.key);
    if (column.type === 'status' && status && value !== null && value !== undefined && value !== '') {
      const held = column.heldByKey ? heldDraftPill(String(value), String(read(row, column.heldByKey) ?? '')) : null;
      if (held) return compactFigure(column, held.text);
      return compactFigure(column, status.resolve(status.entityTypeFor(column.key), String(value))?.label ?? String(value));
    }
    // 🔒 A figure that means nothing without its name carries the column's compact label before it (decision 1544; T813).
    return compactFigure(column, formatCell(column, value, currencyCode, dates));
  };
  /** The Compact row's value, with the caller's mark after it where it names one (decision 1140). */
  const compactValue = (column: ColumnDef, row: R): string => {
    const text = formatCell(column, read(row, column.key), currencyCode, dates);
    const mark = text === '' ? null : cellMark?.(row, column.key) ?? null;
    return mark ? `${text} ${mark}` : text;
  };
  /**
   * 🔒 An `image` cell draws the file at the row's height through the file route the record pages read, a neutral
   * placeholder where the row holds none (T3333; decision 9380). The picture gives way to the placeholder if the file does not stream.
   */
  const renderThumbnail = (column: ColumnDef, row: R): ReactNode => {
    const fileId = read(row, column.key);
    const held = typeof fileId === 'string' && fileId !== '';
    // 🔒 The picture is the thumbnail rendition (decision 6390) and its alt text is the row's primary name (T3356).
    const name = primary ? formatCell(primary, read(row, primary.key), currencyCode, dates) : '';
    return (
      <span className={styles.thumbnail} data-cell-image={column.key} data-image-file={held ? fileId : 'none'}>
        {held ? (
          <ThumbnailPicture src={`${imageBaseUrl}${fileThumbPath(fileId)}`} alt={name !== '' ? name : t('record.picture.alt')} />
        ) : (
          <span className={styles.thumbnailPlaceholder} aria-hidden="true" />
        )}
      </span>
    );
  };
  // 🔨 A currency cell draws its negatives red (decision 0481); every other cell is the text it always was.
  const renderCell = (column: ColumnDef, row: R): ReactNode => {
    if (column.type === 'status') return renderStatus(column, row);
    if (column.type === 'image') return renderThumbnail(column, row);
    // 🔒 A `marks` cell draws the caller's glyphs in place of its words (decision 9720; T3164), the gold dot and a missing form's triangle.
    if (column.type === 'marks') return <span className={styles.marks} data-cell-marks={column.key}>{cellMarker?.(row, column.key) ?? null}</span>;
    // 🔒 A `pills` cell draws one pill per word it holds (T752; decision 1486), such as a company's Customer, Vendor and Manufacturer flags.
    if (column.type === 'pills') {
      const words = String(read(row, column.key) ?? '').split(',').map((word) => word.trim()).filter(Boolean);
      return words.length === 0 ? null : (
        <span className={styles.pills} data-cell-pills={column.key}>
          {words.map((word) => <Pill key={word} text={word} foreground="var(--navy-primary)" background="var(--selection-bg)" size={Size.Small} />)}
        </span>
      );
    }
    const text = formatCell(column, read(row, column.key), currencyCode, dates);
    const mark = text === '' ? null : cellMark?.(row, column.key) ?? null;
    // 🔒 A marked figure keeps its own red and carries the caller's word after it (decision 1140).
    if (column.type === 'currency') return mark ? <><MoneyText text={text} /> <span className={styles.cellMark} data-cell-mark={column.key}>{mark}</span></> : <MoneyText text={text} />;
    /*
     * A link cell points at another record (decision 0565). 🔨 The click is
     * stopped here, or the row's own open would run as well and the person
     * would land on the row they were leaving. A row missing the pattern's
     * fields draws the text alone, so a link is never half an address.
     */
    if (column.type === 'link' && column.linkPattern && onRoute) {
      const route = resolveLinkPattern(column.linkPattern, row as Readonly<Record<string, unknown>>);
      if (route && text)
        return (
          <a
            href={route}
            data-cell-link={column.key}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRoute(route);
            }}
          >
            {text}
          </a>
        );
    }
    const marker = cellMarker?.(row, column.key) ?? null;
    return marker ? <>{text}{marker}</> : text;
  };
  const headless = new Set(groupBy?.headless ?? []);
  /** 🔒 A nested row's lead: the tree list's own chevron on a parent, the same room left blank on a leaf so the names align (decision 8880). */
  const nestLead = (id: string, at: NestedRow<R>): ReactNode =>
    at.isLeaf ? <span className={styles.nestSpacer} aria-hidden="true" /> : <TreeToggle expanded={at.open} onToggle={() => setFolded((was) => toggleExpanded(was, id))} />;
  /** A parent's child count beside its name, open or folded (decision 8880). */
  const nestCount = (at: NestedRow<R>): ReactNode => (at.isLeaf ? null : <TreeChildCount count={at.childCount} />);
  // 🔒 Decision 6211: a group folds to its band, remembered per device, per list and view, and per group value.
  const groupFolds = useFoldMemory(`${LIST_GROUP_FOLD_KEY_PREFIX}${recordType}.${shownViewId}.`);
  const styleOf = (row: R) => rowStyle?.(row) ?? null;
  const rowClass = (row: R): string => [styles.row, styleOf(row)?.className ?? ''].filter(Boolean).join(' ');
  const cellClass = (column: ColumnDef): string =>
    [styles.cell, styles[column.role] ?? '', column.fit ? styles.fit ?? '' : '', alignmentFor(column.type) === 'right' ? styles.right : ''].filter(Boolean).join(' ');

  return (
    <div ref={cardRef} className={styles.card} data-list-viewer="true" data-mode={mode} data-record-type={recordType} data-selecting={selectable && selected > 0 ? 'true' : 'false'}>
      <div className={styles.top}>
        {/* 🔒 T3240: a compact list has no header row, so its select-page box stands at the left of the head, and Select all N matching follows it as in the table. */}
        {mode === 'compact' && selectable && pageIds.length > 0 ? (
          <span className={styles.compactSelectPage} data-select-page="true">
            <Checkbox label={t('list.selectPage')} labelHidden size={Size.Small} checked={allOnPage} indeterminate={someOnPage} onChange={() => dispatch({ type: 'togglePage', pageIds })} />
            <span className={styles.compactPageCount} data-page-count={pageIds.length}>{pageCountText}</span>
          </span>
        ) : null}
        {leading ? <div className={styles.leading} data-list-leading="true">{leading}</div> : null}
        <div className={styles.switcher} data-view-switcher="true">
          <Dropdown size={Size.Large}
            key={shownViewId}
            label={t('list.view')}
            entityLabel={t('list.view')}
            search={searchViews}
            onSelect={(row) => chooseView(row.id)}
            onAddNew={addView}
            {...(viewNotice ? { error: viewNotice } : {})}
            rowLead={(row) => {
              const named = viewNamed(row.id);
              if (!onStarView) return named?.isFavourite ? <Icon meaning="star" size="inline" label={t('view.starred')} /> : null;
              const starLabel = `${t(named?.isFavourite ? 'view.unstar' : 'view.star')} ${named?.name ?? ''}`;
              /* 🔨 The star is a toggle of its own: its press never reaches the row, so starring a view does not choose it (decision 2880). */
              return (
                <span className={named?.isFavourite ? styles.starOn : styles.starOff} data-view-star={row.id} data-starred={named?.isFavourite ? 'true' : 'false'} onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.preventDefault()}>
                  <IconButton meaning="star" size={Size.Small} tone={named?.isFavourite ? 'accent' : 'plain'} pressed={named?.isFavourite === true} label={starLabel} name="view-star" onClick={() => onStarView(row.id)} />
                </span>
              );
            }}
            rowAction={(row) => (editView && viewNamed(row.id)?.editable ? { label: `${t('view.edit')} ${viewNamed(row.id)?.name ?? ''}`, meaning: 'edit', onAct: () => editView(row.id) } : null)}
            minChars={0}
            value={currentView?.name ?? ''}
          />
        </div>
        {filters ? <div className={styles.filters} data-list-filters="true">{filters}</div> : null}
        {/* 🔒 The record count reads only in the footer beside the pager now (decision 1444; T691). */}
        {selectable && selected > 0 ? (
          <span className={styles.selectedCount} data-selection-count={selected}>
            {selectedText}
          </span>
        ) : null}
      </div>
      {selectable && selected > 0 ? (
        <div className={styles.actionBar} role="toolbar" aria-label={t('list.actions')} data-action-bar="true">
          <span>{selectedText}</span>
          {selectionActions ? <span className={styles.selectionActions} data-selection-actions="true">{selectionActions}</span> : null}
          <Button size={Size.Small} color={Color.Inactive} text={[t('list.clear')]} actions={[() => dispatch({ type: 'clear' })]} />
        </div>
      ) : null}
      {selectable && offersSelectAllMatching(selection, pageIds, count) ? (
        <button type="button" className={styles.linkButton} data-select-all-matching="true" onClick={() => dispatch({ type: 'selectAllMatching' })}>
          {selectAllText}
        </button>
      ) : null}
      {mode === 'compact' && !bandless && layout !== 'compact' ? (
        <div className={styles.band} data-band={recordType}>
          {recordType}
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className={styles.scroll}
        data-list-scroll="true"
        onScroll={(event) => {
          standAt.current = event.currentTarget.scrollTop;
          onScrollTop?.(event.currentTarget.scrollTop);
        }}
      >
        {filterNoMatch && (panelState.kind === 'empty' || (panelState.kind === 'ready' && rows.length === 0)) ? (
          <div className={styles.noMatch} data-filter-no-match="true">
            <p className={styles.noMatchText}>
              {recordSingular
                ? `${t('view.noMatch.lead')} ${recordSingular.toLowerCase()} ${t(filterNoMatch.searchOnly ? 'filter.noMatch.searchTail' : 'filter.noMatch.tail')}`
                : t(filterNoMatch.searchOnly ? 'filter.noMatch.searchRows' : 'filter.noMatch.rows')}
            </p>
            <Button size={Size.Small} color={Color.Inactive} text={[t('listFilter.clearAll')]} actions={[filterNoMatch.onClear]} />
            {/* 🔒 T3341: a view that narrows keeps its Edit View link beside the filter's own line. */}
            {viewNarrows && editView && shownView?.editable && !shownView.builtIn ? (
              <button type="button" className={styles.linkButton} data-view-no-match-edit="true" onClick={() => editView(shownViewId)}>
                {t('view.edit')}
              </button>
            ) : null}
          </div>
        ) : viewNarrows && (panelState.kind === 'empty' || (panelState.kind === 'ready' && rows.length === 0)) ? (
          <div className={styles.noMatch} data-view-no-match={shownViewId}>
            <p className={styles.noMatchText}>{recordSingular ? `${t('view.noMatch.lead')} ${recordSingular.toLowerCase()} ${t('view.noMatch.tail')}` : t('view.noMatch.rows')}</p>
            {editView && shownView?.editable && !shownView.builtIn ? (
              <button type="button" className={styles.linkButton} data-view-no-match-edit="true" onClick={() => editView(shownViewId)}>
                {t('view.edit')}
              </button>
            ) : null}
          </div>
        ) : panelState.kind !== 'ready' ? (
          <AsyncStatePanel state={panelState} emptyAction={emptyAction} onRetry={onRetry ?? noop}>
            {() => null}
          </AsyncStatePanel>
        ) : (
          <table ref={tableRef} className={styles.table} data-mode={mode} data-tight={mode === 'wide' && tight ? 'true' : undefined}>
            {mode === 'wide' ? (
              <thead className={styles.head}>
                <tr>
                  {selectable ? (
                    <th scope="col" className={styles.checkCell}>
                      <Checkbox label={t('list.selectPage')} labelHidden size={Size.Small} checked={allOnPage} indeterminate={someOnPage} onChange={() => dispatch({ type: 'togglePage', pageIds })} />
                    </th>
                  ) : null}
                  {columns.map((c) => {
                    const active = sort?.key === c.key;
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        className={cellClass(c)}
                        aria-sort={active && sortLocked === undefined ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                        data-column={c.key}
                        data-role={c.role}
                      >
                        {c.type === 'marks' ? (
                          // 🔒 The Marks column's header names it for a screen reader alone and offers no sort (decision 9720).
                          <span className={styles.hidden}>{c.label}</span>
                        ) : sortLocked !== undefined || c.sortable === false ? (
                          <span className={styles.sortLabel} title={sortLocked} data-sort="locked">
                            {c.label}
                          </span>
                        ) : (
                          <button type="button" className={styles.sortButton} onClick={() => onSortChange(toggleSort(sort, c.key))} data-sort={active ? sort.direction : 'none'}>
                            <span>{c.label}</span>
                            {active ? <Icon meaning={sort.direction === 'asc' ? 'collapse' : 'expand'} size="inline" /> : null}
                          </button>
                        )}
                      </th>
                    );
                  })}
                  {onNavigate ? (
                    <th scope="col" className={styles.navCell}>
                      <span className={styles.hidden}>{t('list.open')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.key}>
                  {groupBy && !headless.has(g.key) ? (
                    <tr className={styles.groupRow} data-group={g.key} data-group-folded={groupFolds.isFolded(g.key) ? 'true' : 'false'}>
                      <td colSpan={mode === 'wide' ? wideSpan : 1}>
                        <span className={styles.groupHead}>
                          <FoldChevron folded={groupFolds.isFolded(g.key)} onToggle={() => groupFolds.toggle(g.key)} name={g.key} />
                          <span>{g.key}</span>
                          {groupSizes ? <span className={styles.groupCount} data-group-count={groupSizes.get(g.key) ?? g.rows.length}>{groupSizes.get(g.key) ?? g.rows.length}</span> : null}
                        </span>
                      </td>
                    </tr>
                  ) : null}
                  {(groupBy && !headless.has(g.key) && groupFolds.isFolded(g.key) ? [] : g.rows).map((row) => {
                    const id = idOf(row);
                    const rowSelected = isSelected(selection, id);
                    if (mode === 'compact') {
                      return (
                        <CompactRow
                          key={id}
                          id={id}
                          style={styleOf(row)}
                          emphasised={isEmphasised?.(row) ?? false}
                          selected={rowSelected}
                          active={activeId !== undefined && activeId !== null && activeId === id}
                          selectable={selectable}
                          onToggle={() => dispatch({ type: 'toggleRow', id })}
                          {...(onNavigate && (opensRow?.(row) ?? true) ? { onOpen: () => onNavigate(row) } : {})}
                          width={width}
                          primary={primary ? formatCell(primary, read(row, primary.key), currencyCode, dates) : ''}
                          primaryMarker={primary ? cellMarker?.(row, primary.key) ?? null : null}
                          {...(nestOf.has(id) ? { nest: { depth: nestOf.get(id)!.depth, lead: nestLead(id, nestOf.get(id)!), count: nestCount(nestOf.get(id)!), expanded: nestOf.get(id)!.isLeaf ? undefined : nestOf.get(id)!.open } } : {})}
                          secondary={secondaries.map((c) => secondaryText(c, row)).filter(Boolean).join(' · ')}
                          secondaryParts={secondaries.map((c) => ({ text: secondaryText(c, row), whole: c.compactWrap === true })).filter((part) => part.text !== '')}
                          wrapSecondary={wrapCompact}
                          picture={imageColumn ? renderThumbnail(imageColumn, row) : null}
                          marks={marksColumn ? cellMarker?.(row, marksColumn.key) ?? null : null}
                          statusPill={statusColumn ? renderStatus(statusColumn, row) : null}
                          value={valueColumn ? compactValue(valueColumn, row) : ''}
                          money={valueColumn?.type === 'currency'}
                        />
                      );
                    }
                    return (
                      <tr
                        key={id}
                        className={rowClass(row)}
                        data-row-id={id}
                        data-row-style={styleOf(row)?.name}
                        aria-selected={selectable ? rowSelected : undefined}
                        data-row-active={activeId !== undefined && activeId !== null && activeId === id ? 'true' : undefined}
                        data-row-emphasis={isEmphasised?.(row) ? 'true' : undefined}
                        {...(onNavigate && (opensRow?.(row) ?? true) ? { onClick: () => onNavigate(row), 'data-row-opens': 'true' } : {})}
                        {...(nestOf.has(id) ? { 'aria-level': nestOf.get(id)!.depth + 1, ...(nestOf.get(id)!.isLeaf ? {} : { 'aria-expanded': nestOf.get(id)!.open }) } : {})}
                        {...(expandRow
                          ? {
                            onClick: () => expandRow.onToggle(row),
                            onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                              if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
                                // A row that carries aria-expanded toggles on Space too, and Space must not scroll the page (T2488).
                                event.preventDefault();
                                expandRow.onToggle(row);
                              }
                            },
                            tabIndex: 0,
                            'aria-expanded': expandRow.isOpen(row),
                            'data-row-opens': 'true',
                            'data-row-expanded': expandRow.isOpen(row) ? 'true' : 'false',
                          }
                          : {})}
                      >
                        {/* 🔨 The selection cell swallows its own click, so ticking a
                            row never opens it (piece 4). */}
                        {selectable ? (
                          <td className={styles.checkCell} onClick={(event) => event.stopPropagation()}>
                            <Checkbox label={t('list.selectRow')} labelHidden size={Size.Small} checked={rowSelected} onChange={() => dispatch({ type: 'toggleRow', id })} />
                          </td>
                        ) : null}
                        {columns.map((c) => {
                          const at = c.role === 'primary' ? nestOf.get(id) : undefined;
                          return (
                            <td
                              key={c.key}
                              className={at ? `${cellClass(c)} ${styles.nested}` : cellClass(c)}
                              data-column={c.key}
                              {...(at ? { style: nestStyle(at), 'data-nest-depth': at.depth } : {})}
                              {...(c.type === 'text' ? cellTitleProps(cellTitle?.(row, c.key) ?? null, formatCell(c, read(row, c.key), currencyCode, dates)) : {})}
                            >
                              {at ? <span className={styles.nestCell}>{nestLead(id, at)}<span className={styles.nestText}>{renderCell(c, row)}</span>{nestCount(at)}</span> : renderCell(c, row)}
                            </td>
                          );
                        })}
                        {onNavigate ? (
                          <td className={styles.navCell}>
                            {(opensRow?.(row) ?? true) ? <button type="button" className={styles.iconButton} aria-label={t('list.open')} onClick={() => onNavigate(row)}>
                              <Icon meaning="navigateInto" size="inline" />
                            </button> : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
            {footRows.length > 0 ? (
              <tfoot>
                {footRows.map((row) =>
                  mode === 'compact' ? (
                    <CompactRow
                      key={idOf(row)}
                      id={idOf(row)}
                      style={styleOf(row)}
                      foot
                      selected={false}
                      selectable={selectable}
                      onToggle={noop}
                      width={width}
                      primary={primary ? formatCell(primary, read(row, primary.key), currencyCode, dates) : ''}
                      primaryMarker={primary ? cellMarker?.(row, primary.key) ?? null : null}
                      secondary={secondaries.map((c) => secondaryText(c, row)).filter(Boolean).join(' · ')}
                      secondaryParts={secondaries.map((c) => ({ text: secondaryText(c, row), whole: c.compactWrap === true })).filter((part) => part.text !== '')}
                      wrapSecondary={wrapCompact}
                      statusPill={null}
                      value={valueColumn ? compactValue(valueColumn, row) : ''}
                      money={valueColumn?.type === 'currency'}
                    />
                  ) : (
                    // 🔒 The foot is drawn by the same cells as every row, so a total reads the way its column reads (decisions 0564, 0880).
                    <tr key={idOf(row)} className={rowClass(row)} data-row-foot="true" data-row-style={styleOf(row)?.name}>
                      {selectable ? <td className={styles.checkCell} /> : null}
                      {columns.map((c) => (
                        <td key={c.key} className={cellClass(c)} data-column={c.key}>
                          {renderCell(c, row)}
                        </td>
                      ))}
                      {onNavigate ? <td className={styles.navCell} /> : null}
                    </tr>
                  ),
                )}
              </tfoot>
            ) : null}
          </table>
        )}
      </div>
      <div className={styles.footer}>
        <label className={styles.pageSize}>
          <span>{t('list.pageSize')}</span>
          {/* The closed box is the Dropdown's, chevron and all (decision 1450). */}
          <span className={fieldStyles.selectBox}>
            <select
              className={fieldStyles.input}
              value={pageSize}
              data-page-size="true"
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value) as PageSize);
                goToPage(1);
              }}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <SelectChevron />
          </span>
        </label>
        <span className={styles.footerCount} data-record-count={count}>{countText}</span>
        <span className={styles.pager}>
          <button type="button" className={styles.iconButton} aria-label={t('list.previous')} disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)} data-page-previous="true">
            <Icon meaning="previous" size="inline" />
          </button>
          <span data-page-of={pages}>{pageText}</span>
          <button type="button" className={styles.iconButton} aria-label={t('list.next')} disabled={currentPage >= pages} onClick={() => goToPage(currentPage + 1)} data-page-next="true">
            <Icon meaning="next" size="inline" />
          </button>
        </span>
      </div>
      {viewStore ? <SavedViewWindow store={store} columns={allColumns} plural={recordType} sort={sort} pageSize={pageSize} /> : null}
      {viewStore && inRouter ? <SavedViewAddress value={store.addressed} onChange={store.setAddressed} {...(viewStore.param ? { param: viewStore.param } : {})} /> : null}
    </div>
  );
}

interface CompactRowProps {
  id: string;
  /** The caller's named style for the row (decision 0880). */
  style?: { name: string; className: string } | null;
  /** A foot row: no selection, no open, and marked as the foot. */
  foot?: boolean;
  emphasised?: boolean;
  selected: boolean;
  /** 🔒 The row the detail pane is showing (decision 0318), marked on a compact row as on a table row; the pinned Items pane needs it (decision 2852). */
  active?: boolean;
  /** False on a list that takes no selection (T349): the row draws no checkbox. */
  selectable?: boolean;
  onToggle: () => void;
  /** Opens the record. Absent where the caller passed no navigate handler. */
  onOpen?: () => void;
  width: number | null;
  primary: string;
  /** T3028: the caller's mark after the primary line, such as the gold dot. */
  primaryMarker?: ReactNode;
  /** 🔒 A nested row's depth, chevron and child count (decision 8880). */
  nest?: { depth: number; lead: ReactNode; count: ReactNode; expanded: boolean | undefined };
  secondary: string;
  /** T3761: the same line as its figures, so one a column marks `compactWrap` stays whole (no break inside a phone number or a time) while the line wraps between figures. */
  secondaryParts?: readonly { text: string; whole: boolean }[];
  statusPill: ReactNode;
  value: string;
  /** 🔒 The value column is typed currency, the one text this row scans for a negative figure (decision 2155). */
  money?: boolean;
  /** 🔒 Decision 1544: a secondary line carrying compact labels wraps so every named figure shows, rather than ending in an ellipsis. */
  wrapSecondary?: boolean;
  /** 🔒 The row's picture, at the left of its text, where the list ticks an `image` column (T3333; decision 9380). */
  picture?: ReactNode;
  /** 🔒 The row's marks, at its far left after the checkbox, where the list carries a `marks` column (decision 9720; T3164). */
  marks?: ReactNode;
}

/** A list thumbnail: the picture, or the grey placeholder when the file does not stream (T3356). */
function ThumbnailPicture({ src, alt }: { src: string; alt: string }) {
  // 🔒 The failure belongs to the file it was read for (T3448): a row reused for another file tries that file's picture.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;
  if (failed) return <span className={styles.thumbnailPlaceholder} aria-hidden="true" data-thumbnail-failed="true" />;
  return <img className={styles.thumbnailPicture} src={src} alt={alt} onError={() => setFailedSrc(src)} />;
}

/**
 * A Compact row: the primary line on its own line, with no flag dot (decision 1402), then the
 * secondary line and the pill-and-value unit sharing one line while they fit
 * and taking a third line, the unit right-aligned as one group, only when
 * they would collide (piece 4, overlap-aware collapse).
 */
function CompactRow({ id, selected, active = false, selectable = true, onToggle, onOpen, width, primary, primaryMarker = null, nest, secondary, secondaryParts, statusPill, value, money = false, emphasised = false, style = null, foot = false, wrapSecondary = false, picture = null, marks = null }: CompactRowProps) {
  const lineRef = useRef<HTMLDivElement>(null);
  const secondaryRef = useRef<HTMLSpanElement>(null);
  const unitRef = useRef<HTMLSpanElement>(null);
  const [lines, setLines] = useState<2 | 3>(2);
  useLayoutEffect(() => {
    const line = lineRef.current;
    const s = secondaryRef.current;
    const u = unitRef.current;
    if (!line || !s || !u) return;
    setLines(compactLineCount(s.scrollWidth, u.scrollWidth, line.clientWidth));
  }, [width, secondary, value]);
  return (
    <tr
      className={[styles.row, style?.className ?? ''].filter(Boolean).join(' ')}
      data-row-id={foot ? undefined : id}
      data-row-foot={foot ? 'true' : undefined}
      data-row-style={style?.name}
      aria-selected={foot || !selectable ? undefined : selected}
      {...(nest ? { 'aria-level': nest.depth + 1, 'aria-expanded': nest.expanded } : {})}
      data-lines={lines}
      data-row-emphasis={emphasised ? 'true' : undefined}
      data-row-active={active ? 'true' : undefined}
      {...(onOpen ? { onClick: onOpen, 'data-row-opens': 'true' } : {})}
    >
      <td className={styles.compactCell}>
        <div className={nest ? `${styles.compactRow} ${styles.compactNested}` : styles.compactRow} {...(nest ? { style: { '--tree-depth': nest.depth } as CSSProperties, 'data-nest-depth': nest.depth } : {})}>
          {selectable ? (
            <span className={styles.compactCheck} onClick={(event) => event.stopPropagation()}>
              {foot ? null : <Checkbox label={t('list.selectRow')} labelHidden size={Size.Small} checked={selected} onChange={onToggle} />}
            </span>
          ) : null}
          {marks ? <span className={styles.compactMarks} data-compact-marks="true">{marks}</span> : null}
          {picture ? <span className={styles.compactPicture}>{picture}</span> : null}
          <div className={styles.compactBody}>
            <div className={styles.compactPrimary} data-compact-primary="true">
              {nest?.lead}
              <span className={styles.compactPrimaryText}>{primary}</span>
              {primaryMarker}
              {nest?.count}
            </div>
            {onOpen ? (
              <button
                type="button"
                className={styles.compactOpen}
                aria-label={t('list.open')}
                // The row beneath opens too; this fires once, not once per layer.
                onClick={(event) => { event.stopPropagation(); onOpen(); }}
                data-compact-open="true"
              />
            ) : null}
            <div ref={lineRef} className={styles.compactLine} data-lines={lines}>
              <span ref={secondaryRef} className={styles.compactSecondary} data-compact-secondary="true" data-compact-wrap={wrapSecondary ? 'true' : undefined}>
                {secondaryParts
                  ? secondaryParts.map((part, index) => (
                      <Fragment key={index}>
                        {index > 0 ? ' · ' : null}
                        {part.whole ? <span className={styles.compactWhole}>{part.text}</span> : part.text}
                      </Fragment>
                    ))
                  : secondary}
              </span>
              <span ref={unitRef} className={styles.compactUnit} data-compact-unit="true">
                {statusPill}
                {value ? <span className={styles.compactValue}>{money ? <MoneyText text={value} /> : value}</span> : null}
              </span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}
